// Constants used across the scoring system. Derived from the entity
// enums in docs/score/architecture.md.

// #2786 — the ONE canonical dressage judge display/posting order for the
// whole system (arena left to right, and the printed signature-line order).
// USEF Rulebook Chapter DR, DR123.2 (public scoreboard) and DR123.3
// (Individual Classification / final posted results), both verbatim: "When
// multiple judges officiate from different positions, scores must be posted
// in the following order: E, H, C, M, B." This is federation-mandated, not a
// display preference. Source: docs/rules/usef/rulebook-dr-dressage-2026.pdf,
// (c) USEF 2026 (see docs/rules/README.md for the inventory entry). Before
// this card the value here was ["C", "E", "M", "B", "H"] (the old printed
// signature-line order, no rule backing found for it), and five other files
// had grown private copies of the correct E,H,C,M,B order; this is now the
// single source, consumed via resolveClassPanel and sortJudgesForDisplay
// below (utils/calculation.js).
export const JUDGE_POSITIONS = ["E", "H", "C", "M", "B"];

// Card #1750 (equitation class grid), settled per #1748's review thread: a
// panel-consensus sheet (DSE/DSM, 2-3 co-signing judges) has no single real
// judge identity to key ScoreSheet.judgeId on, but that field is a required,
// non-nullable abstract booth-position slot already (see JUDGE_POSITIONS and
// Class.scribeAssignments), not a real judge id — confirmed by
// offloadPanelSignature's own comment in apps/score-api/src/lib/signatures.js.
// Every ScoreSheet the class grid creates for a panel-consensus test is keyed
// to this fixed slot, matching the young-horse precedent of hard-coding "C"
// as the technical-judge convention position. This closes the #1748 seam
// (isAssignedScribeForSheet's pre-panel-branch check) by construction: an
// admin setting up a panel-consensus class must assign the scribe to
// position C. See docs/score/equitation-class-sheet-design.md sections 5-6.
export const PANEL_CONSENSUS_JUDGE_POSITION = "C";

// Card #1992 — "presiding judge" is defined on the card as judge position C,
// always, for any panel size. Distinct from PANEL_CONSENSUS_JUDGE_POSITION
// above (same value, different concept: that one is the fixed seat a
// panel-consensus sheet is co-signed under; this one is the seat that alone
// may raise the scribe scoresheet's No Show action). Kept as its own named
// export so a reader of either call site sees which rule it's citing.
export const PRESIDING_JUDGE_POSITION = "C";

export const PERSON_TYPES = {
  RIDER: "RIDER",
  JUDGE: "JUDGE",
  SCRIBE: "SCRIBE",
};

// #768 — ENTERING_RING sits between PENDING and IN_PROGRESS: C's "Bell"
// press (#767's arena-entry timer) persists it the instant the horse starts
// its approach, before any ScoreSheet exists to derive IN_PROGRESS off of.
// "In Ring" then persists IN_PROGRESS the same way. Both are stored, not
// derived, so every surface (not just C) can see a ride has started — see
// score-common/utils/ride-status.js's deriveRideStatus for how this is
// surfaced pre-sheet, and ride-queue.js's isCurrentCandidate for why it also
// counts as "the ride in the ring" for ring-queue/next-ride purposes.
export const RIDE_STATUSES = {
  PENDING: "PENDING",
  ENTERING_RING: "ENTERING_RING",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  SCRATCHED: "SCRATCHED",
};

// Card #916 (part A) — a SCRATCHED ride can optionally carry a term reason,
// literally FVD's own native tblRide.chrTermReason strings (confirmed on the
// card). Card #1073 (separate) writes these back to FVD via foxbox; this repo
// only persists them on the Ride so that later write-back has something to
// send. Keep these exact strings — they are NOT freely relabelable.
export const RIDE_TERM_REASONS = {
  SCRATCH: "Scratch",
  NO_SHOW: "No Show",
};

// Card #2053 (part B, workstream scoring-truth, split from #2017/#1998 supercard
// item 8) — the federation's own dressage-ride-termination vocabulary, per
// USEF Rulebook Chapter DR, DR123.3 (Scoring, Classification and Prize-Giving),
// confirmed against the committed PDF (docs/rules/usef/rulebook-dr-dressage-2026.pdf)
// by card #2052 (part A), recorded in docs/score/term-reason-vocabulary.md §1:
// "the words 'scratched', 'excused', 'eliminated', or 'no show' ... must appear
// after the competitor's name in the result sheet." Exactly these four labels —
// NOT Evin's originally proposed "eliminated / withdrew / retired / excused"
// (DR123.3 prints "scratched" as the noun, not "withdrew"; "retired" has no
// DR-chapter equivalent at all, see the doc's §1 table).
//
// This is a DISTINCT field from RIDE_TERM_REASONS above: that one is the
// pre-existing #916 bidirectional FVD wire passthrough (raw FVD
// tblRide.chrTermReason strings on ingest/egress, "NOT freely relabelable" per
// its own comment) and must not be renamed, repurposed, or widened. This new
// vocabulary lives on ride.terminationReason (see schemas/validation.js and
// utils/ride-status.js), never on ride.termReason.
export const TERM_REASONS = {
  SCRATCHED: "Scratched",
  EXCUSED: "Excused",
  ELIMINATED: "Eliminated",
  NO_SHOW: "No Show",
};

// Card #567 — FVD write-back (push) status, persisted on a Ride only by the
// one endpoint that owns this contract (score-api's reportFvdPushStatus,
// handlers/ride.js): foxbox reports POSTED once it's written a released
// ride's score into FVD's .sdf, or FAILED with an error if that write
// attempt itself failed. This enum deliberately has no PENDING/NOT_POSTED
// member of its own — an unreported ride simply carries no fvdPushStatus key
// at all (undefined, never fabricated/defaulted to POSTED). That per-RIDE
// absence is what feeds the per-CLASS NOT_POSTED aggregate below.
export const FVD_PUSH_STATUSES = {
  POSTED: "POSTED",
  FAILED: "FAILED",
};

// Card #567 — aggregate FVD push state for a Class, computed at READ time
// (getClass/listClasses) over the class's RELEASED rides only (see
// isRideReleased in utils/ride-status.js) — never persisted on the Class
// itself. Consumed by the #554 scoreadmin-fe icon: gray = NOT_POSTED
// (covers both "no released rides yet" and "released rides that simply
// haven't all reported POSTED"), green = POSTED (every released ride
// reported POSTED), red = FAILED (at least one released ride reported
// FAILED, regardless of the rest). See computeClassPushState.
export const CLASS_PUSH_STATES = {
  NOT_POSTED: "NOT_POSTED",
  POSTED: "POSTED",
  FAILED: "FAILED",
};

// Card #2147 — the announcer day spine's `placed` marker, one bit per class,
// for an entire ring in a single call (score-api's getClassResultsSummary,
// GET /class-results-summary?showId=&ring=). Deliberately coarser than the
// per-judge PARTIAL/CONFIRMED/FINAL vocabulary above (DERIVED_RIDE_STATUSES)
// and independent of it: a class is PLACED once isClassPlaced (utils/
// ride-status.js) says every one of its Rides is resolved (a real placing,
// SCRATCHED, a resolvedIntoRideId placeholder, or any DR123.3 term reason),
// the same "class closed" definition the rider self-service sheet gate
// already uses — NOT_PLACED otherwise, including a class with zero Rides
// (isClassPlaced's own empty-list rule). No PARTIAL tier here on purpose:
// the spine only needs to know whether the class is done, never the
// in-between judge-by-judge state, and the summary response never carries
// placings (score-api results.js's own comment on the route).
export const CLASS_RESULTS_STATUSES = {
  PLACED: "PLACED",
  NOT_PLACED: "NOT_PLACED",
};

// #1268 removed force-final entirely (card #506/#1070's admin override):
// nothing ever read the resulting ride.adminForceFinalTreatment
// (fetchConfirmedOrCompletedSheets drops an unconfirmed sheet regardless of
// what was stored), so the FORCE_FINAL_TREATMENTS enum that used to live
// here is gone along with the affordance. Existing prod rows and audit
// entries created before this card are untouched (see #1268's card
// comments for the prod count); ride.adminConfirmedAt/adminConfirmedBy
// themselves stay (packages/score-common/src/schemas/validation.js,
// utils/ride-status.js) because a ride force-finaled before this card still
// needs to read back as FINAL.

// Utility events (card #139) — welfare flags (BLOOD/LAMENESS) a scribe raises
// mid-ride. Persisted on the Ride (utilityEvents array), never on a sheet, so
// every judge position scoring the same ride sees the same flags. Internal
// coordination only — MUST stay off the competitor score sheet, every
// competitor-facing report, and the public rider portal (score.foxvillage.com).
// MEETING_REQUESTED (card #1991) reuses the identical raise/clear plumbing —
// not a welfare flag, but a scribe flagging that a panel meeting is wanted;
// gated FE-side to panel-judged classes only (see utilityEventTypes.js).
export const UTILITY_EVENT_TYPES = {
  BLOOD: "BLOOD",
  LAMENESS: "LAMENESS",
  MEETING_REQUESTED: "MEETING_REQUESTED",
};

export const SYNC_STATUSES = {
  PENDING: "PENDING",
  SYNCING: "SYNCING",
  SYNCED: "SYNCED",
  ERROR: "ERROR",
};

export const SYNC_ACTIONS = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
};

// #772 (workstream error-deductions, #771-774) — canonical per-class
// error-of-course/test deduction scheme enum. #771's deduction ENGINE and
// #773's scribe APPLY both import this same constant — keep the values
// stable once #772 ships. Human-facing labels live in scoreadmin-fe's i18n
// bundle (never persist the label text itself). POINTS is the default:
// USEF's traditional Points 2/4/elim scale is what every class used before
// this setting existed, so classes lacking an explicit selection (and all
// newly created classes) fall back to it.
export const ERROR_DEDUCTION_SCHEMES = {
  POINTS: "POINTS",
  PERCENT_SENIOR: "PERCENT_SENIOR",
  PERCENT_YOUTH: "PERCENT_YOUTH",
};

// USDF score scale: 0–10, half-point granularity.
export const SCORE_MIN = 0;
export const SCORE_MAX = 10;
export const SCORE_STEP = 0.5;

// Tenth-point (0.1) score granularity. Used by:
//   - #712 freestyle ARTISTIC marks (Test.movements[].section === "artistic").
//   - #889 (bugs #70/#72) every mark of a FEI Young Horse / USEF Four-Year-Old
//     test, which are judged to the tenth across the whole sheet.
// All other marks keep SCORE_STEP. Callers pick the step via
// scoreStepFor(test, section) (utils/scoring.js) and pass it through
// isValidScore/clampScore/autoFormatScore.
export const TENTH_SCORE_STEP = 0.1;

// Back-compat alias: freestyle artistic marks were the original tenth-step
// caller (#712). Prefer TENTH_SCORE_STEP / scoreStepFor for new code.
export const ARTISTIC_SCORE_STEP = TENTH_SCORE_STEP;

// Remarks character caps. R1-UI-012 enforces these on the FE; R1-PLAN-007
// gates the API on the same constants when the API exists (Phase 2). The
// character-counter surfaces when the remaining-chars count drops to
// REMARKS_COUNTER_THRESHOLD or below.
export const REMARKS_MAX_PER_MOVEMENT = 500;
export const REMARKS_MAX_GENERAL = 2000;
export const REMARKS_COUNTER_THRESHOLD = 50;

// #2013 — Score Admin Detailed-view annotation text cap. Every other
// free-text field on ScoreSheet is bounded (REMARKS_MAX_PER_MOVEMENT,
// REMARKS_MAX_GENERAL above); scoreSheetAnnotationSchema.text originally
// shipped with only a min(1), no max. Mirrored into the zod schema
// (score-common/src/schemas/validation.js) and the route's body schema
// maxLength (score-api POST /scoresheet/:id/annotations).
export const ANNOTATION_TEXT_MAX = 1000;

// Card #1648 (workstream `scoreboard`) — originally the 7 public-scoreboard
// mode types (docs/score/scoreboard-design.md §6.2). CORRECTION (round-3
// judge finding on #1648): the claim this comment used to make, that no
// MODE_TYPES vocabulary existed anywhere before this card, was FALSE — #1639
// (rock-on/q3, already merged) shipped apps/score-fe/src/components/
// scoreboard/modeTypes.js's `MODE_TYPE` map first, with kebab-case values
// ("class-summary-detailed" etc.), and its own header says those are "the
// kebab-case string values mode.modeType actually carries". This card had
// independently invented a SCREAMING_SNAKE second vocabulary on the same wire
// field, which is a value-incompatible collision, not a duplicate: after
// merge, whichever spelling actually flows over modeType makes the OTHER
// side's every mode-type comparison false (isSponsorCapable/composeTypedFields
// on the score-api side, or SponsorStrip/QRCorner's fails-closed gates on the
// FE side). The kebab-case spelling wins because it is already pinned by
// shipped Cypress specs that hardcode the literal (e.g.
// apps/score-fe/cypress/e2e/scoreboard-screenshots.cy.js's
// `modeType: "end-of-class-result"`) — those specs assert against the real
// wire value, so the spelling can't move without breaking shipped coverage.
// score-common is now the single source of truth: score-fe's modeTypes.js
// re-exports MODE_TYPES from here (as `MODE_TYPE`) instead of keeping its
// own copy. Keys are numbered per the design doc's own §6.2 list for
// traceability.
//
// Card #1794 (2026-08-20 pare-down, workstream `scoreboard`) — types 1, 2, 4,
// 6 (CLASS_SUMMARY_DETAILED, CLASS_SUMMARY_SIMPLE, MID_RIDE_SIMPLE,
// MID_CLASS_RESULT) are REMOVED. They are not renumbered away: the surviving
// keys below keep their original §6.2 numbers in their comments (3, 5, 7) so
// the gaps stay visible, because a stored ScoreboardView.modes[].modeType
// value referencing one of the four removed kebab-case strings can still
// exist in DynamoDB (dev/stage/main scanned on this card, finding stale rows:
// dev/main/demo one each, stage zero; see the card's own comment log — the
// fallback is load-bearing since stale rows do exist, as an already-existing
// one is as real as a future one). Renumbering would silently repoint a
// stored number at a different type; leaving a gap does not. Any config still
// naming a removed type resolves to no known layout family
// (layoutFamilyForModeType in score-fe) and therefore renders Blank, never a
// crash (#1798's universal-fallback rule) — see boardRenderState.js's
// UNKNOWN_MODE case, unchanged by this card, which already covered "modeType
// resolves to nothing" before this card existed.
export const MODE_TYPES = {
  RIDE_INTRO_DETAILED: "ride-intro-detailed", // §6.2 type 3
  POST_RIDE_SCORE: "post-ride-score", // §6.2 type 5
  END_OF_CLASS_RESULT: "end-of-class-result", // §6.2 type 7
  // Card #1795, the 2026-08-20 pare-down — Message: configured text (on the
  // mode itself, see MESSAGE_TEXT_MAX_LENGTH below), centred on an otherwise
  // empty board. §6.2's numbered list ran 1-7 and #1797 already added BLANK
  // OUTSIDE that numbering rather than renumbering the shipped 1-7
  // spellings (which are pinned by Cypress literals, see this file's own
  // header comment). This card's hard requirement 2 was "pick the next free
  // number and say why" — 8 is the next free number in that same §6.2
  // sequence (1-7 used, with 1/2/4/6 removed by #1794 and never reused), so
  // MESSAGE is numbered 8 even though it postdates and isn't itself part of
  // the original design doc's 7-item list, same footing BLANK already has
  // minus the explicit number. Deliberately absent from MODE_TYPE_FIELDS
  // (below) and SPONSOR_CAPABLE_MODE_TYPES, mirroring BLANK exactly:
  // Message's one piece of config (its text) lives directly on the mode,
  // resolved client-side, never through composeTypedFields/`fields` — it
  // performs no show/class/ride data lookup at all (card #1795 AC4), so
  // score-api never needs a case for it either.
  MESSAGE: "message", // #1795, next free number after §6.2's 1-7 = 8
  // Card #1797, the 2026-08-20 pare-down — Blank: a configured (or, absent
  // that, the Fox Village default) logo, centred on an otherwise empty
  // board. Not part of §6.2's original numbered 7; added outside that
  // numbering rather than renumbering the shipped 1-7 spellings above,
  // which are pinned by Cypress literals per this file's own header
  // comment. Deliberately absent from MODE_TYPE_FIELDS (below) and from
  // SPONSOR_CAPABLE_MODE_TYPES — Blank composes no fields at all (its one
  // piece of config, `logoAsset`, lives directly on the mode, resolved
  // client-side against score-fe's own small allow-list, never through
  // composeTypedFields) and is never sponsor/QR-capable. score-api's
  // composeTypedFields (scoreboard-public.js) already falls through to
  // `return undefined` for any modeType it doesn't explicitly compose, and
  // score-fe's modeTypes.js gates (sponsorVariantForModeType/
  // isQrCapableModeType) already fail closed for a type absent from their
  // maps — BLANK needs no new case in either place. It IS the universal
  // "nothing to show" fallback (#1798, Jaybill 2026-08-20: "NEVER show an
  // error message on the scoreboard"), so score-fe's BlankLayout renders it
  // from nothing more than this modeType + an optional logoAsset string.
  BLANK: "blank",
};

// Card #2314 — per-CLASS result-layout choice for the END_OF_CLASS_RESULT
// view, offered from the scoreboard remote. DECIDED BY JAYBILL 2026-09-05:
// operator-picked, never auto-detected (a heuristic that guesses wrong on a
// live board can't be corrected). This is deliberately a per-class layout
// choice on the one existing view type, not a new MODE_TYPE and not general
// field config (see docs/score/scoreboard-design.md's #2314 revision entry,
// which records this as a deliberate revision of the earlier "five view
// types, no field configuration" call).
//
// BY_DIVISION is today's existing RankedListLayout behaviour (grouped by
// entryIdentity.riderStatus, #1945) and is both the DEFAULT and the fallback
// (AC4): stored on `Class.resultLayout`, an unset or unrecognised value must
// degrade to BY_DIVISION, never an error and never a blank board — same
// fails-closed doctrine as isKnownTermReason below. COMBINED renders the
// class's whole ranked list as one table, no DIVISION heading, regardless of
// whether the data carries a riderStatus.
export const RESULT_LAYOUTS = {
  BY_DIVISION: "by-division",
  COMBINED: "combined",
};

export function isKnownResultLayout(value) {
  return Object.values(RESULT_LAYOUTS).includes(value);
}

// AC4 — the single fails-closed resolver every reader of a stored
// Class.resultLayout must go through, so "unset" and "unrecognised" collapse
// to the exact same default rather than each call site re-deciding.
export function resolveResultLayout(value) {
  return isKnownResultLayout(value) ? value : RESULT_LAYOUTS.BY_DIVISION;
}

// #1795 — the character cap the remote's Message text field enforces
// (`maxLength`), and the ceiling MessageLayout.jsx's length-tiered font-size
// scaling was sized against. Decision recorded on the card and in the
// ModeEditorDialog/MessageLayout commit: wrap-and-scale-to-a-floor, backed by
// this hard cap as the second layer, rather than either alone — a pure
// character cap with no scaling would still let a merely-long-but-under-cap
// message overflow at full size, and pure JS-measured "shrink until it fits"
// was rejected as fragile on kiosk hardware (font-loading races) and gives
// the remote operator no signal of what's too long until it's already live
// on the board. Ellipsis truncation was also rejected: silently cutting an
// operational message ("Ring 1 resumes at 1:30 — please...") can drop the
// actionable half of the sentence, worse than the operator having to shorten
// it up front. 240 is generous enough for a couple of short sentences while
// keeping every tier's floor font-size still "by far the largest type on the
// board" (card #1795 AC3).
export const MESSAGE_TEXT_MAX_LENGTH = 240;

// design §6.2 — the exact field-key list per mode type. Per §6.1's shared
// EntryIdentity component, `entryIdentity` bundles CNO + riderName +
// riderStatus + horseName as ONE field, not four. `startList`/`rankedList`
// are likewise single fields bundling their per-row shape (chrono row /
// ranked row respectively) — the design offers "the list", not its
// constituent columns. `banner` (types 6/7's fixed "current class standings"
// / "final class results" copy) is NOT listed here — it is inherent to the
// type, never toggled.
//
// Card #1799 (2026-08-20, "kill per-mode field selection") — this stopped
// being PICKER vocabulary. There is no more per-view `fields[]` selection:
// a view of a given mode type renders EVERY field its type defines, always.
// This constant is now simply the field set of the type — the exhaustive
// list score-api's composeSelectedFields (lib/scoreboard-mode-payload.js)
// composes from, unconditionally, whenever the underlying data resolves. A
// key still ends up absent from the composed payload only when its
// candidate value doesn't exist for this class/entry (design §1's "missing
// data is omitted, never placeholdered" — unchanged by this card), never
// because a view chose not to select it. A stored ScoreboardView.modes[]
// row may still carry a `fields[]` array from before this card (existing
// dev/stage/main rows may); it is simply ignored on read rather than
// migrated/stripped, since #1799 was framed as "for now," which argues for
// reversibility over destroying the data — see the card's own decision
// log. Two of the keys below are structurally unproducible by
// composeSelectedFields and are never emitted under a `fields` key at all:
// `sponsor` lives in the envelope (`sponsor` at the top level of
// getPublicScoreboard's response, gated by isSponsorCapable below, never
// inside `payload.fields`), and `qr` is resolved client-side (design §6.3)
// with nothing for the server to compose. For types listing these keys
// (POST_RIDE_SCORE and END_OF_CLASS_RESULT), a composed `fields` object's
// key set is always a SUBSET of this list for the type, never equal to it.
// RIDE_INTRO_DETAILED has no unproducible keys, so its composed set can
// equal the type's list. Assert accordingly when testing composeSelectedFields's
// output against this constant.
export const MODE_TYPE_FIELDS = {
  [MODE_TYPES.RIDE_INTRO_DETAILED]: [
    "classNumberName",
    "testName",
    "entryIdentity",
    "riderLocation",
    "horseBio",
    "ownerInfo",
  ],
  [MODE_TYPES.POST_RIDE_SCORE]: [
    "classNumberName",
    "testName",
    "entryIdentity",
    "scoreHero",
    // Card #2312 — the DR123.3 outcome (Scratched/Excused/Eliminated/No
    // Show) for a terminated ride, same field name and same score-common
    // read path #2060/#2104 already put on RankedList/ClassResultsPage.
    // This is the one type those two cards left uncovered: the post-ride
    // single-entry screen had no way to show the outcome, so a terminated
    // ride's composite-0 scoreHero either printed "0.000%" (pre-#2285) or,
    // after #2285's below-55 suppression, dropped the whole right column
    // with nothing in its place. Composed only when a recognised term
    // reason resolves (see isKnownTermReason, score-common/utils/
    // ride-status.js) — never a fifth vocabulary, never the legacy #916
    // `ride.termReason` wire field.
    "termReason",
    "currentRank",
    // Card #2282 — division-scoped sibling to currentRank, same {rank,
    // ofCount} shape, composed only when the ride's division resolves.
    "divisionRank",
    "perJudgeBreakout",
    "qr",
  ],
  [MODE_TYPES.END_OF_CLASS_RESULT]: [
    "classNumberName",
    "judgesPositions",
    "rankedList",
    // Card #2314 — the per-class layout choice (RESULT_LAYOUTS above),
    // always resolved (never absent) via resolveResultLayout, so this key
    // is the one exception to this list's "subset, not equal" comment above
    // for this type: it's always present once composeEndOfClassResult runs.
    "resultLayout",
    "sponsor",
    "qr",
  ],
  // MODE_TYPES.BLANK is intentionally NOT a key here. Every lookup against
  // this map already falls back to [] for a missing key (score-api's
  // allowedFieldsFor, `MODE_TYPE_FIELDS[modeType] || []`), which is exactly
  // right — its one piece of config (`logoAsset`) lives directly on the mode
  // object, not inside `fields`.
};

// design §7.1 originally listed 4 sponsor-capable types (the summary/result
// families); card #1794 removes 3 of them (CLASS_SUMMARY_DETAILED,
// CLASS_SUMMARY_SIMPLE, MID_CLASS_RESULT all left MODE_TYPES entirely), so
// END_OF_CLASS_RESULT is the sole survivor. Every other type is still nulled
// server-side rather than trusting the client to ignore it ("the server
// composes, it never trusts the client to enforce a rule").
export const SPONSOR_CAPABLE_MODE_TYPES = [MODE_TYPES.END_OF_CLASS_RESULT];

// #1755 — lifted out of scribe-fe's ScoringForm.jsx (#499 freestyle
// Technical/Artistic, #542 young-horse Technical/Quality), so any FE
// grouping a test's movements by section (scribe's ScoringForm AND
// scoreadmin-fe's RideEditor) renders the identical STATIC i18n key for a
// given section value instead of each surface keeping its own copy that
// can drift. Each app's own locale bundle still owns the translated text
// at this key path (both currently resolve it under a top-level "scoring"
// namespace) — this map only pins which key a section maps to.
export const FREESTYLE_SECTION_LABEL_KEY = {
  technical: "scoring.technicalExecution",
  artistic: "scoring.artisticImpression",
  // #542 — young-horse / Children tests split technical vs quality; label
  // the quality section header when it renders.
  quality: "scoring.qualityImpression",
};

// #1756 - lifted from scribe-fe's ScoringForm.jsx (#208, split by #753),
// which is where these strings were authored, and where scoreadmin-fe's
// RideEditor had a hand-mirrored second copy with a comment saying so.
// Two copies of a preset list is two lists: the scribe and the admin
// eliminate a ride for "Rider Fall" only for as long as nobody edits one
// file and not the other.
//
// ⚠️ Only scoreadmin-fe renders THIS array today. #1756 deleted RideEditor's
// mirrored copy and pointed it here, but scribe-fe still declares its own
// literal ELIMINATION_REASONS in ScoringForm.jsx (re-pointing a live kiosk
// file was out of scope for that card). So two copies still exist and can
// still drift; what changed is that the drift is now between two files
// instead of three, and this one is the copy to edit.
//
// These are the reasons a human picks from, not a rule schedule: none of
// them carries a figure, and picking one never computes a deduction. The
// error/deduction FIGURES live in scoring.js (SCHEME_TIERS,
// FREESTYLE_ERROR_TIERS), cited to USEF DR122.5 there. An eliminate-tier
// error auto-eliminates (DR122.5.b.3) — scribe-fe/scoreadmin-fe push a
// fromError:true eliminations[] entry themselves when a tier resolves to
// one (#1831, revisiting the #682 2026-07-15 MVP deferral); this array is
// only the reasons a human picks by hand for every OTHER elimination cause.
//
// Neither surface renders these through i18n today (scribe-fe's #208 menu
// and RideEditor's own copy were both raw English strings); keeping them
// raw here is a like-for-like lift, not a new decision. The free-text
// "Custom reason…" fallback each surface already offers is what covers a
// reason this list does not.
// #1992 — "No Show", raised only by the scribe scoresheet's presiding-judge
// (position C) action, which waterfalls it onto the ride as a whole rather
// than one judge's own sheet (see score-api's ride.js markRideNoShow). Kept
// as its OWN named export, deliberately NOT a member of ELIMINATION_REASONS
// below: that array is a flat free-pick list every renderer of it treats as
// "any of these, offered with no further gate" (RideEditor's per-row menu,
// today; any future consumer that iterates the array gets the same free
// pick). Appending this value there and filtering it back out at the one
// current renderer inverts that contract — the array itself would silently
// carry a value no consumer is allowed to free-pick, a trap for the next
// reader who adds one. Any surface that offers "No Show" as a reason must
// go through the dedicated C-only action (score-api's ride.js
// markRideNoShow) and import this constant directly, never discover it by
// iterating ELIMINATION_REASONS.
export const NO_SHOW_ELIMINATION_REASON = "No Show";

// Card #2017 AC8 — a per-sheet elimination whose reason is exactly this
// string derives TERM_REASONS.SCRATCHED, not ELIMINATED, in
// deriveEliminationsTermReason below (utils/ride-status.js) — unless a
// genuine elimination cause is ALSO present, in which case the more severe
// outcome wins (see that function's own doc comment for the precedence
// rule). USEF Rulebook Chapter DR, © USEF 2026,
// docs/rules/usef/rulebook-dr-dressage-2026.pdf, verified against the
// committed PDF: "If a competitor withdraws (scratches) ... the words
// 'scratched' ... must appear after the competitor's name" — DR123.3, not a
// DR124 elimination CAUSE at all.
//
// Card #2063 (untangling this card #2052/#2053 flagged and #2017 left open,
// docs/score/term-reason-vocabulary.md §1a) — REMOVED from ELIMINATION_REASONS
// below: "Withdrawn / retired" is one of DR123.3's own outcome labels, not a
// DR124 elimination cause, so it no longer belongs in a cause-only pick
// list. The constant itself stays exported and is still load-bearing:
// deriveEliminationsTermReason (utils/ride-status.js) matches this string
// (case/punctuation-insensitively as of card #2340, alongside bare
// "withdrawal"/"withdrawn"/"withdraw" — a real prod row, entry 791, stored
// exactly the lowercase one-word form) to resolve tier 4 of
// deriveRideTermReason's 4-tier fallback for any ScoreSheet already
// carrying it as a stored elimination reason (pre-#2063 data, including
// rides among the #2056 backfill's un-migrated set) — removing the picker
// option does not and must not remove the read path for data that already
// used it. A scribe who needs to record an in-progress withdrawal now uses
// the dedicated Scratch gavel-menu action instead (#916 part A / card
// #2063 AC1, ScoringForm.jsx's "Scratch ride" section, score-api's
// setRideStatus) — this constant is kept purely for reading old sheets,
// never offered as a fresh pick again.
export const WITHDRAWAL_ELIMINATION_REASON = "Withdrawn / retired";

export const ELIMINATION_REASONS = [
  "Errors of course",
  "Off course",
  // #753 - split from the old combined "Fall of horse or rider" preset so
  // either (or both, as two separate entries) can be recorded.
  "Horse Fall",
  "Rider Fall",
  "Lameness",
  "Blood",
  "Equipment violation",
  // #2063 — "Withdrawn / retired" removed (see WITHDRAWAL_ELIMINATION_REASON's
  // own comment above for why it doesn't belong in a DR124 cause list, and
  // where a scribe now records a withdrawal instead).
];

// #1756 - how an `errors[]` entry's deduction amount was arrived at.
//
// RULE: resolved from the ride's error-of-course tier schedule
// (`resolveErrorTiers` → `errorTierForOccurrence`). Every entry scribe-fe's
// `recordError` writes is this, and it is the DEFAULT for an entry carrying
// no `amountSource` at all - which is every error stored before this card,
// so nothing already on a sheet changes meaning.
//
// MANUAL: an admin typed the amount by hand in scoreadmin's RideEditor.
// That surface is a deliberate escape hatch, and the point of it is
// recording what the schedule would NOT produce, so a MANUAL entry is never
// re-priced or re-labelled by `retierRuleErrors`, and never consumes an
// error-of-course occurrence slot (an admin line reading "Late entry −2"
// must not push the next real error of course onto the 2nd tier).
export const ERROR_AMOUNT_SOURCES = {
  RULE: "rule",
  MANUAL: "manual",
};

// Card #2000 — the Class Placings screen's four view modes (#945/#953),
// moved here from scoreadmin-fe's ClassPlacings.jsx so score-api's
// setShowDefaultPlacingView can validate a show-level default against the
// SAME four strings the FE renders, with one definition instead of two
// copies that could drift. See show-config.js's setShowDefaultPlacingView
// for the resolution order (class-level session choice, when the operator
// has picked one on THIS page load, wins; else Show.defaultPlacingView,
// when set; else the system default, card #2001) and
// ClassPlacings.jsx's own resolvePlacingView for where it's applied.
export const PLACING_VIEWS = {
  DEFAULT: "default",
  SCORE: "score",
  DIVISION: "division",
  SUGGESTED: "suggested",
};

// #1934 - what is currently in a ring, stored (not derived) so the
// scoreboard/announcer never have to guess. Three statuses, not four
// (docs/score/ring-state.md): a ring that has finished a class keeps
// reporting RUNNING with that class until something replaces it - it is
// never a fourth "empty" state, on purpose (spectators are reading the
// class that just ended). BREAK/END_OF_DAY are exclusively explicit: no
// ride completing announces either one, so nothing may infer them.
export const RING_STATUS = {
  RUNNING: "RUNNING",
  BREAK: "BREAK",
  END_OF_DAY: "END_OF_DAY",
};

// #1934 - who last set a ring's state. SCORED is the ordinary path (a ride
// being scored writes it, costing the scribe nothing extra); EXPLICIT is an
// intentional override (Start class #1935, or Score Admin #1936/BREAK/END
// OF DAY declarations). The distinction is what lets an EXPLICIT "Start
// class" survive a stray score from the outgoing class instead of being
// clobbered by the very next write (docs/score/ring-state.md "class
// boundary" rule).
export const RING_STATE_SOURCE = {
  SCORED: "SCORED",
  EXPLICIT: "EXPLICIT",
};

// #2182 - the breed-show-scoring workstream's class-kind signal (gate card
// for #2183/#2184/#2185/#2186). FVD stamps a DSHB class with its QUALIFYING
// class's test id, so the test alone cannot tell a championship class from
// an ordinary one (see classifyDshbClassKind in
// ./utils/dshb-class-kind.js). Five kinds classify from the className/
// fvdTestId heuristic alone, matching USEF DR204/DR211: an ordinary
// (non-DSHB-special) class classifies as `null` from the heuristic, not a
// sixth heuristic value — callers that only care about these five kinds
// get an unambiguous falsy "not one of these" rather than another string
// to special-case.
export const DSHB_CLASS_KINDS = {
  CHAMPIONSHIP: "CHAMPIONSHIP",
  MATERIALE: "MATERIALE",
  SUITABILITY: "SUITABILITY",
  UNDER_SADDLE: "UNDER_SADDLE",
  // #2202 - a USDF Breeders Championship SERIES FINAL class (USEF DR211.2:
  // "At least two judges judging independently, using individual score
  // sheets, are required for USDF Breeders Championship classes"),
  // distinct from an ordinary CHAMPIONSHIP class (which stays on #2183's
  // place-only class grid, DR211.1) and from a USDFBC QUALIFYING round
  // (which is stamped with the same "champ"-pattern className but is still
  // an ordinary single-judge CHAMPIONSHIP class — DR210.5's qualifying-
  // championship clause never invokes DR211.2's two-judge floor). See
  // classifyDshbClassKind's own doc comment for the real-daysheet name
  // signal that tells a Final apart from a Qualifier.
  USDFBC_CHAMPIONSHIP: "USDFBC_CHAMPIONSHIP",
  // #2729 - the escape hatch: an explicit per-class dshbKindOverride value
  // meaning "this is not a breeding class at all, stop guessing from the
  // name". Unlike the five kinds above, classifyDshbClassKind's className/
  // fvdTestId heuristic never RETURNS this on its own - it only ever
  // arrives via dshbKindOverride (classifyDshbClassKind's `if
  // (dshbKindOverride) return dshbKindOverride` short-circuit, dshb-class-
  // kind.js), so every consumer that switches on specific kinds
  // (isDshbPlaceOnlyGridKind/isDshbScoredGridKind, the CHAMPIONSHIP/
  // USDFBC_CHAMPIONSHIP checks in isDshbHandlerClass and score-api/
  // report-api's callers) already falls through to "not DSHB" for it with
  // no code change, because none of those checks match this string. The
  // stored value MUST be exactly "NOT_DSHB": 13 real prod
  // score-class-foxvillage-main rows (classes 515/516/517/524/525/526/531/
  // 532/534/535/536/537/538) were already written with this exact string
  // as the 2026-09-17 live-show firedrill mitigation, before this enum
  // member existed to make it a supported value (card #2729).
  NOT_DSHB: "NOT_DSHB",
};

// #2730 - which of classifyDshbClassKind's two signals actually decided a
// class's kind (./utils/dshb-class-kind.js's classifyDshbClassKindWithSource).
// The card's own real case, class 534 "Great American/USDF Fourth Level FS
// A/A Champ" on test usdf-freestyle-4th-2023, is a normal Fourth Level
// Freestyle that matched CHAMPIONSHIP on the word "Champ" alone - the
// classifier's className/fvdTestId heuristic is intentionally kept (real
// daysheets carry DSHB classes with no other signal but the name), but
// nothing told a human the decision came from the name rather than a
// resolved test id, so a wrong guess routed a live ring to the #2183
// place-only grid with no way to spot or override it. NAME covers every
// name-pattern branch (CHAMPIONSHIP, USDFBC_CHAMPIONSHIP, SUITABILITY) -
// even USDFBC_CHAMPIONSHIP, which additionally checks the resolved test id
// against USDFBC_ELIGIBLE_FVD_TEST_IDS, is reached only because a name
// pattern matched first. TEST_ID covers MATERIALE/UNDER_SADDLE, which carry
// no distinguishing word at all and are resolved purely off fvdTestId. A
// `dshbKindOverride` win, or no kind resolved at all, reports `null` -
// neither signal decided it.
export const DSHB_CLASS_KIND_SIGNAL_SOURCES = {
  NAME: "NAME",
  TEST_ID: "TEST_ID",
};

// #2251 - the two independent signals reconcile can fire for
// Class.testDisagreement (apps/score-api/src/handlers/daysheet.js). Neither
// signal ever changes ride.testId (still the sole authoritative test edge,
// #435) - both are read-only diagnostics recorded so the FE (#2252) can say
// WHY a class was flagged.
//   CLASS_RIDE_MISMATCH - the class carries a real (non fvd-unmatched:*)
//     bound testId AND at least one of its rides resolves to a different
//     real testId. Never fires for a Test-of-Choice class: TOC's class-level
//     testId is the fvd-unmatched:9999 sentinel by design, so it never
//     qualifies as "real" here.
//   DSHB_NAME_MISMATCH - classifyDshbClassKind (./utils/dshb-class-kind.js),
//     given the class's own bound test, disagrees with the SAME function
//     given one of the class's rides' resolved test (same className both
//     times - only the test id input changes). Gated behind the class
//     itself carrying a real bound testId (same TOC exemption as above), so
//     it can never fire alone for a TOC class either.
export const TEST_DISAGREEMENT_SIGNALS = {
  CLASS_RIDE_MISMATCH: "CLASS_RIDE_MISMATCH",
  DSHB_NAME_MISMATCH: "DSHB_NAME_MISMATCH",
};

// #2362 (scribe-presence workstream, card A) — the scribe-fe heartbeat
// contract. Shared here (not just score-api-local) because the sender
// (#2364, scribe-fe) needs the SAME interval the write endpoint's TTL is
// sized against, per that card's AC4 ("the interval is a named constant, not
// a magic number"). Exported from score-common rather than duplicated so the
// two apps can never drift apart.
//
// PRESENCE_TTL_SECONDS is deliberately a multiple of the interval (4x), not
// an unrelated round number: it must survive a couple of missed/delayed
// beats (a slow network, a backgrounded tab) without either flapping an
// instance to "gone" between heartbeats, or leaving a truly-dead instance
// looking alive for long after it stopped. Consumers (#2363 and after) still
// own the "is this actually offline" DISPLAY threshold on top of this raw
// TTL — see the card #2362 description's "presence is an observation, not a
// judgement" rule; this constant only bounds how long a row survives with no
// further writes.
export const HEARTBEAT_INTERVAL_MS = 15000;
export const PRESENCE_TTL_SECONDS = 60;
